import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { updateSearchParameters } from "client_legacy/ndaw/urlHelper";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { isNil, isNotNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { processSseStream, SseItem } from "./sseParser";

export interface HttpRequestOptions {
	async?: boolean;
	cache?: boolean;
	signal?: AbortSignal;
}

export class HttpClient {
	public get<TSuccess>(
		url: string,
		data?: object | null,
		options: HttpRequestOptions = {}
	): Promise<NoInfer<TSuccess>> {
		return this.execute(
			{
				method: "GET",
				url,
				headers: createMimeTypeHeaders(JSON_MIME_TYPE),
				queryParameters: data,
				deserializationMethod: "json"
			},
			options
		);
	}

	public async getSse<TSuccess>(
		url: string,
		data?: object | null,
		options: HttpRequestOptions = {}
	): Promise<AsyncIterable<NoInfer<TSuccess>>> {
		const intermediateResult: AsyncIterable<SseItem> = await this.execute<AsyncIterable<SseItem>>(
			{
				method: "GET",
				url,
				headers: createMimeTypeHeaders(SSE_MIME_TYPE),
				queryParameters: data,
				deserializationMethod: "sse"
			},
			options
		);

		return mapSseItemToJson(intermediateResult);
	}

	public getText(
		url: string,
		data?: object | null,
		options: HttpRequestOptions = {}
	): Promise<string> {
		return this.execute(
			{
				method: "GET",
				url,
				headers: createMimeTypeHeaders(TEXT_MIME_TYPE),
				queryParameters: data,
				deserializationMethod: "text"
			},
			options
		);
	}

	public getBlob(
		url: string,
		data?: object | null,
		options: HttpRequestOptions = {}
	): Promise<Blob> {
		return this.execute(
			{
				method: "GET",
				url,
				headers: createMimeTypeHeaders(ANY_MIME_TYPE),
				queryParameters: data,
				deserializationMethod: "none"
			},
			options
		);
	}

	public post<TSuccess>(
		url: string,
		data?: any,
		options: HttpRequestOptions = {}
	): Promise<NoInfer<TSuccess>> {
		const { body, bodyMimeType } = prepareJsonBody(data);

		return this.execute(
			{
				method: "POST",
				url,
				headers: createMimeTypeHeaders(
					JSON_MIME_TYPE,
					bodyMimeType
				),
				body,
				deserializationMethod: "json"
			},
			options
		);
	}

	public async postAndReceiveSse<TSuccess>(
		url: string,
		data?: any,
		options: HttpRequestOptions = {}
	): Promise<AsyncIterable<NoInfer<TSuccess>>> {
		const { body, bodyMimeType } = prepareJsonBody(data);

		const intermediateResult: AsyncIterable<SseItem> = await this.execute<AsyncIterable<SseItem>>(
			{
				method: "POST",
				url,
				headers: createMimeTypeHeaders(
					SSE_MIME_TYPE,
					bodyMimeType
				),
				body,
				deserializationMethod: "sse"
			},
			options
		);

		return mapSseItemToJson(intermediateResult);
	}

	public postFormData<TSuccess>(
		url: string,
		data: FormData,
		options: HttpRequestOptions = {}
	): Promise<NoInfer<TSuccess>> {
		return this.execute(
			{
				method: "POST",
				url,
				headers: createMimeTypeHeaders(JSON_MIME_TYPE),
				body: data,
				deserializationMethod: "json"
			},
			options
		);
	}

	public put<TSuccess>(
		url: string,
		data?: any,
		options: HttpRequestOptions = {}
	): Promise<NoInfer<TSuccess>> {
		const { body, bodyMimeType } = prepareJsonBody(data);

		return this.execute(
			{
				method: "PUT",
				url,
				headers: createMimeTypeHeaders(
					JSON_MIME_TYPE,
					bodyMimeType
				),
				body,
				deserializationMethod: "json"
			},
			options
		);
	}

	public delete<TSuccess>(
		url: string,
		data?: any,
		options: HttpRequestOptions = {}
	): Promise<NoInfer<TSuccess>> {
		const { body, bodyMimeType } = prepareJsonBody(data);

		return this.execute(
			{
				method: "DELETE",
				url,
				headers: createMimeTypeHeaders(
					JSON_MIME_TYPE,
					bodyMimeType
				),
				body,
				deserializationMethod: "json"
			},
			options
		);
	}

	public static registerResponseHandler(
		statusCode: number,
		handler: HttpResponseHandler
	): (() => void) {
		return registerResponseHandler(statusCode, handler);
	}

	public static registerBeforeRequestHandler(
		handler: OnBeforeHttpRequestHandler
	): (() => void) {
		return registerBeforeRequestHandler(handler);
	}

	protected async execute<TResult>(
		request: UnpreparedHttpRequestData,
		options: HttpRequestOptions,
	): Promise<TResult> {
		const url = new URL(request.url, window.origin);

		if (request.queryParameters) {
			updateSearchParameters(url.searchParams, request.queryParameters);
		}

		const headers = new Headers(request.headers);

		// Mimic the behavior of jquery (our backend has code for it)
		if (!headers.has("X-Requested-With")) {
			headers
				.set("X-Requested-With", "XMLHttpRequest");
		}

		const preparedRequest: HttpRequestData = {
			method: request.method,
			url,
			headers,
			body: request.body
		};

		triggerBeforeRequestHandlers(preparedRequest);

		const executeOptions: ExecuteOptions = {
			cache: options.cache ?? false,
			signal: options.signal,
			deserializationMethod: request.deserializationMethod
		};

		let executionResult: ExecutionResult;
		if (options.async === false) {
			executionResult = syncExecute(preparedRequest, executeOptions);
		} else {
			executionResult = await asyncExecute(preparedRequest, executeOptions);
		}

		const {
			statusCode
		} = executionResult;

		triggerResponseHandlers(statusCode);

		const isOk: boolean = statusCode >= 200 && statusCode < 300;

		if (!isOk) {
			const responseBody: TryResult<string | undefined> = await tryReadFullBodyAsText(executionResult);
			throw new HttpRequestError(
				statusCode,
				responseBody.wasSuccessful
					? responseBody.result ?? ""
					: ""
			);
		}

		switch (request.deserializationMethod) {
			case "json":
			case "text":
				const responseBody: TryResult<string | undefined> = await tryReadFullBodyAsText(executionResult);

				if (!responseBody.wasSuccessful) {
					throw new HttpRequestError(
						statusCode,
						""
					);
				}

				if (isNil(responseBody.result)) {
					return undefined as TResult;
				}

				return deserializeResponse(
					statusCode,
					responseBody.result,
					request.deserializationMethod
				);
			case "sse":
				if (executionResult.contentType) {
					if (executionResult.contentType.toLowerCase() !== SSE_MIME_TYPE) {
						throw new HttpRequestError(
							statusCode,
							`Expected Content-Type '${SSE_MIME_TYPE}' but got '${executionResult.contentType}'`
						);
					}
				}

				const stream: ReadableStream<string> = createUtf8StringStream(executionResult.body);

				return processSseStream(stream.getReader()) as TResult;
			case "none":
				const result: TryResult<Blob | undefined> = await tryReadFullBodyAsBlob(executionResult);

				if (!result.wasSuccessful) {
					throw new HttpRequestError(statusCode, "");
				}

				return (result.result ?? new Blob()) as TResult;
			default:
				never(request.deserializationMethod);
		}
	}
}

export class EnvironmentHttpClient extends HttpClient {
	protected override execute<TResult>(
		request: UnpreparedHttpRequestData,
		options: HttpRequestOptions
	): Promise<TResult> {
		return super.execute(
			{
				...request,
				url: createUrlRelativeToEnvironment(request.url)
			},
			options
		);
	}
}

export class HttpRequestError extends Error {
	private readonly _statusCode: number;
	private readonly _body: string;

	public get statusCode(): number { return this._statusCode; }
	public get body(): string { return this._body; }

	constructor(statusCode: number, body: string) {
		super(`The request did not succeed. Code: ${statusCode}`);

		this._statusCode = statusCode;
		this._body = body;
	}

	public readJson<T>(): T {
		return JSON.parse(this._body) as T;
	}
}

export const environmentHttpClient = new EnvironmentHttpClient();

export type HttpRequestData = {
	method: string;
	url: URL;
	headers: Headers;
	body: string | FormData | nil;
};

export type OnBeforeHttpRequestHandler = (request: HttpRequestData) => void;
export type HttpResponseHandler = () => void;

const SSE_MIME_TYPE = "text/event-stream";
const TEXT_MIME_TYPE = "text/plain";
const JSON_MIME_TYPE = "application/json";
const ANY_MIME_TYPE = "*/*";

function createMimeTypeHeaders(
	acceptMimeType: string,
	bodyMimeType?: string | undefined
): Record<string, string> {
	const result: Record<string, string> = {
		"Accept": acceptMimeType
	};

	if (isNotNil(bodyMimeType)) {
		result["Content-Type"] = `${bodyMimeType}; charset=utf-8`;
	}

	return result;
}

type PreparedJsonBody =
	| { body: undefined; bodyMimeType: undefined; }
	| { body: string; bodyMimeType: typeof JSON_MIME_TYPE; };

function prepareJsonBody(data: any) : PreparedJsonBody {
	if (isNotNil(data)) {
		return {
			body: JSON.stringify(data),
			bodyMimeType: JSON_MIME_TYPE
		};
	}

	return {
		body: undefined,
		bodyMimeType: undefined
	};
}

type DeserializationMethod =
	| "text"
	| "json"
	| "sse"
	| "none";

type UnpreparedHttpRequestData = {
	method: string;
	url: string;
	queryParameters?: object | null;
	headers: Record<string, string>;
	body?: string | FormData | nil;
	deserializationMethod: DeserializationMethod;
};

const onBeforeRegisteredHandlers: OnBeforeHttpRequestHandler[] = [];

function registerBeforeRequestHandler(handler: OnBeforeHttpRequestHandler): (() => void) {
	const registration: OnBeforeHttpRequestHandler = (x) => handler(x);
	return addWithRemoveHandlerResult(onBeforeRegisteredHandlers, registration);
};

function triggerBeforeRequestHandlers(request: HttpRequestData): void {
	if (onBeforeRegisteredHandlers.length === 0) {
		return;
	}

	for (const handler of [...onBeforeRegisteredHandlers]) {
		handler(request);
	}
}

const registeredResponseHandlers = new Map<number, HttpResponseHandler[]>();

function registerResponseHandler(
	statusCode: number,
	handler: HttpResponseHandler
): (() => void) {
	const registration: HttpResponseHandler = (): void => handler();

	let handlers: HttpResponseHandler[] | undefined = registeredResponseHandlers.get(statusCode);

	if (!handlers) {
		handlers = [];
		registeredResponseHandlers.set(statusCode, handlers);
	}

	return addWithRemoveHandlerResult(handlers, registration);
}

function triggerResponseHandlers(statusCode: number): void {
	const handlers: HttpResponseHandler[] | undefined = registeredResponseHandlers.get(statusCode);

	if (!handlers || handlers.length === 0) {
		return;
	}

	for (const handler of [...handlers]) {
		handler();
	}
}

function addWithRemoveHandlerResult<TElement extends Function>(
	collection: TElement[],
	element: TElement
): (() => void) {
	collection.push(element);

	return (): void => {
		const index: number = collection.indexOf(element);
		if (index !== -1) {
			collection.splice(index, 1);
		}
	};
}

type ExecuteOptions = {
	cache: boolean;
	signal?: AbortSignal;
	deserializationMethod: DeserializationMethod;
};

type ExecutionResult = {
	statusCode: number;
	body: ReadableStream<Uint8Array> | null;
	contentType: string | null;
};

async function asyncExecute(
	requestData: HttpRequestData,
	options: ExecuteOptions
): Promise<ExecutionResult> {
	let response: Response;

	try {
		response = await fetch(
			requestData.url,
			{
				body: requestData.body,
				headers: requestData.headers,
				method: requestData.method,
				credentials: "include",
				cache: options.cache
					? "default"
					: "no-store",
				signal: options.signal
			}
		);
	} catch (e) {
		if (isCancelledError(e)) {
			throw e;
		}

		const message: string = e instanceof Error
			? e.message
			: "Failed to make request";

		console.error(message);

		throw new HttpRequestError(-1, "");
	}

	const responseContentType: string | null = response.headers
		.get("Content-Type");

	return {
		statusCode: response.status,
		contentType: responseContentType,
		body: response.body ?? null
	};
}

// NOTE: sync execute does not respect the (abort) signal. The thread (event loop) is blocked
function syncExecute(
	requestData: HttpRequestData,
	options: ExecuteOptions
): ExecutionResult {
	if (!options.cache) {
		requestData.url
			.searchParams
			.append("_", Date.now().toString());
	}

	const xhr = new XMLHttpRequest();

	xhr.open(
		requestData.method,
		requestData.url,
		/* async: */ false
	);

	requestData.headers.forEach(
		(value, key) => {
			xhr.setRequestHeader(key, value);
		}
	);

	// We cannot set the responseType to "arraybuffer" because doing a sync with a non-empty responseType is not allowed.
	// So we use the tricked described in the blog post, were we use a charset that uses the "UNICODE Private Area" and
	// convert those characters back to their raw byte values using a mask
	// https://web.archive.org/web/20071103070418/http://mgran.blogspot.com/2006/08/downloading-binary-streams-with.html
	xhr.overrideMimeType("text/plain; charset=x-user-defined");

	xhr.send(requestData.body ?? null);

	const responseBodyAsText: string = xhr.responseText;

	const responseBodyAsBytes: Uint8Array = new Uint8Array(responseBodyAsText.length);
	for (let i = 0; i < responseBodyAsText.length; i++) {
		responseBodyAsBytes[i] = responseBodyAsText.charCodeAt(i) & 0xFF;
	}

	const contentType: string | null = xhr.getResponseHeader("Content-Type");

	return {
		statusCode: xhr.status,
		contentType,
		body: createStreamFromBytes(responseBodyAsBytes)
	};
}

function createStreamFromBytes(value: Uint8Array): ReadableStream<Uint8Array> {
	return new ReadableStream<Uint8Array>({
		pull: (controller) => {
			if (value) {
				controller.enqueue(value);
			}
			controller.close();
		}
	});
}

function deserializeResponse<TResult>(
	statusCode: number,
	body: string,
	deserializationMethod: Exclude<DeserializationMethod, "sse" | "none">
): TResult {
	try {
		const deserializer: (responseText: string) => any = deserializers[deserializationMethod];
		return deserializer(body) as TResult;
	} catch (e) {
		if (isCancelledError(e)) {
			throw e;
		}

		const message: string = e instanceof Error
			? e.message
			: "Failed to read/deserialize the response body";

		console.error(message);

		throw new HttpRequestError(statusCode, body);
	}
}

const deserializers: Record<Exclude<DeserializationMethod, "sse" | "none">, (text: string) => any> = {
	["json"]: (x) => JSON.parse(x),
	["text"]: (x) => x,
};

async function* mapSseItemToJson<TSuccess>(
	input: AsyncIterable<SseItem>
): AsyncIterable<TSuccess> {
	const deserializer: (responseText: string) => any = deserializers.json;
	for await (const item of input) {
		yield deserializer(item.data) as TSuccess;
	}
}

async function tryReadFullBodyAsBlob(
	executionResult: ExecutionResult
): Promise<TryResult<Blob | undefined>> {
	const {
		body,
		contentType
	} = executionResult;

	if (body) {
		try {
			const parts: BlobPart[] = [];

			const sink = new WritableStream<Uint8Array>({
				write(chunk) {
					parts.push(
						toArrayBufferBackedArray(chunk)
					);
				},
			});

			await body.pipeTo(sink);

			const result = new Blob(
				parts,
				{
					type: contentType ?? "application/octet-stream"
				}
			);

			return { wasSuccessful: true, result };
		} catch (e) {
			if (isCancelledError(e)) {
				throw e;
			}

			return { wasSuccessful: false };
		}
	} else {
		return { wasSuccessful: true, result: undefined };
	}
}

function toArrayBufferBackedArray(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
	if (bytes.buffer instanceof ArrayBuffer) {
		return new Uint8Array(
			bytes.buffer,
			bytes.byteOffset,
			bytes.byteLength
		);
	}

	const result = new Uint8Array(bytes.byteLength);
	result.set(bytes);
	return result;
}

function createUtf8StringStream(
	byteStream: ReadableStream<Uint8Array> | null
): ReadableStream<string> {
	return (byteStream ?? createStreamFromBytes(new Uint8Array()))
		.pipeThrough(
			new TransformStream<Uint8Array, BufferSource>({
				transform(chunk, controller) {
					controller.enqueue(
						toArrayBufferBackedArray(chunk)
					);
				}
			})
		)
		.pipeThrough(new TextDecoderStream("utf-8"));
}

async function readAllText(stream: ReadableStream<string>): Promise<string> {
	const reader: ReadableStreamDefaultReader<string> = stream.getReader();

	let result: string = "";

	for (; ;) {
		const readResult: ReadableStreamReadResult<string> = await reader.read();

		if (isNotNil(readResult.value)) {
			result += readResult.value;
		}

		if (readResult.done) {
			break;
		}
	}

	return result;
}

async function tryReadFullBodyAsText(
	executionResult: ExecutionResult
): Promise<TryResult<string | undefined>> {
	const {
		contentType,
		body
	} = executionResult;

	if (contentType) {
		try {
			const stream: ReadableStream<string> = createUtf8StringStream(body);

			const text: string = body
				? await readAllText(stream)
				: "";

			return { wasSuccessful: true, result: text };
		} catch (e) {
			if (isCancelledError(e)) {
				throw e;
			}

			return { wasSuccessful: false };
		}
	} else {
		return { wasSuccessful: true, result: undefined };
	}
}
